"use client";
import * as React from "react";
import { useState, useContext, useEffect } from "react";
import Button from "../Button/index";
import { PaginateProps } from "../types";
import styles from "./index.module.css";
import PaginateContext from "./paginate.context";

function Paginate(props: PaginateProps) {
  const [_offset, set_offset] = useState(() => 0);

  function isPaginationHidden() {
    return _offset === 0 && props.currentPageItemsCount < props.itemsPerPage;
  }

  function isPreviousDisabled() {
    return _offset === 0;
  }

  function isNextDisabled() {
    return props.currentPageItemsCount < props.itemsPerPage;
  }

  function handlePreviousClick() {
    const currentOffset = _offset;
    const newOffset = currentOffset - props.itemsPerPage;
    set_offset(newOffset);

    // Update query string in URL
    typeof props.handlePageChange === "function" &&
      props.handlePageChange({
        offset: newOffset,
      });
    // Trigger data re-fetch with new offset
    typeof props.reFetch === "function" &&
      props.reFetch({
        offset: newOffset,
        limit: props.itemsPerPage,
        pageToken: props.pageTokenMap[newOffset - props.itemsPerPage],
      });
  }

  function handleNextClick() {
    const currentOffset = _offset;
    const newOffset = currentOffset + props.itemsPerPage;
    set_offset(newOffset);
    // Update query string in URL
    typeof props.handlePageChange === "function" &&
      props.handlePageChange({
        offset: newOffset,
      });
    // Trigger data re-fetch with new offset
    typeof props.reFetch === "function" &&
      props.reFetch({
        offset: newOffset,
        limit: props.itemsPerPage,
        pageToken: props.pageTokenMap[currentOffset],
      });
  }

  function offsetFromBrowserQS() {
    const offsetFromQueryParams = new URLSearchParams(
      window.location.search
    ).get("offset");
    if (offsetFromQueryParams && Number.isFinite(+offsetFromQueryParams)) {
      return Math.abs(+offsetFromQueryParams);
    }
    return null;
  }

  function reFetchUsingBrowserQS() {
    const _offsetFromBrowserQS = offsetFromBrowserQS();
    if (typeof _offsetFromBrowserQS === "number") {
      // console.log(`offset ${_offsetFromBrowserQS} found in url`);
      set_offset(_offsetFromBrowserQS);
      // console.log(`fetching with offset ${_offsetFromBrowserQS}`);
      typeof props.reFetch === "function" &&
        props.reFetch({
          offset: _offsetFromBrowserQS,
          limit: props.itemsPerPage,
        });
    } else {
      // console.log(`no offset found in url, setting offset to 0 in url`);
      typeof props.handlePageChange === "function" &&
        props.handlePageChange({
          offset: 0,
        });
      // console.log(`fetching with offset 0`);
      typeof props.reFetch === "function" &&
        props.reFetch({
          offset: 0,
          limit: props.itemsPerPage,
        });
    }
  }

  useEffect(() => {
    // console.log(`adding popstate event listener`);
    window.addEventListener("popstate", reFetchUsingBrowserQS);
    reFetchUsingBrowserQS();
  }, []);

  useEffect(() => {
    return () => {
      // console.log(`removing popstate event listener`);
      window.removeEventListener("popstate", reFetchUsingBrowserQS);
    };
  }, []);

  return (
    <PaginateContext.Provider
      value={{
        offset: _offset,
      }}
    >
      <>
        {props.children}
        {!isPaginationHidden() ? (
          <nav aria-label="Pagination Navigation">
            <ul className={styles.ul}>
              <li>
                <Button
                  name="Prev"
                  variant="outline"
                  icon="LeftArrowIcon"
                  aria-label="Goto Previous Page"
                  handleClick={handlePreviousClick}
                  disabled={isPreviousDisabled()}
                />
              </li>
              <li>
                <Button
                  name="Next"
                  variant="outline"
                  icon="RightArrowIcon"
                  aria-label="Goto Next Page"
                  handleClick={handleNextClick}
                  disabled={isNextDisabled()}
                />
              </li>
            </ul>
          </nav>
        ) : null}
      </>
    </PaginateContext.Provider>
  );
}

export default Paginate;
